/**
 * Desktop (lg and up) layout of the future-dates modal: a table of every selected line's ship
 * dates and quantities on the left, and on the right a date picker for `rows[0]` with Apply and
 * "Remove Future Date". State and handlers come from `FutureDatesModal.tsx`; the mobile
 * counterpart is `FutureDatesMobile.tsx`, and a change to what is shown belongs in both.
 */
import { Flex, FlexItem } from "@ryerson/frontend.layout";
import { Typography } from "@ryerson/frontend.typography";
import React, { RefObject } from "react";
import { Modal } from "@ryerson/frontend.modal";
import { FutureDatesType } from "./FutureDatesModal";
import { useTheme } from "@ryerson/frontend.theme";
import { IndexedLayers } from "@ryerson/frontend.common";
import { Table, TableBody, TableCell, TableHead, TableRow } from "@ryerson/frontend.table";
import { css } from "@emotion/react";
import styled from "@emotion/styled";
import { FutureDatesContent } from "./FutureDatesContent";
import { getDisplayUom, useApplication } from "@ryerson/frontend.application";
import { Button } from "@ryerson/frontend.button";
import { DatePickerPopper } from "../DatePickerPopper/DatePickerPopper";
import { getShipDates } from "@components/Helper/DeliveryDateAndMinMaxRange";
import { datePickerStyle } from "@components/Helper/commonStyles";
import { Alert } from "@ryerson/frontend.notification";
import { Icon } from "@ryerson/frontend.assets";
import { useEcommerce } from "@ryerson/frontend.e-commerce";
import { isShoppingCartDataType } from "@components/Helper/TypeHelpers";
import { CustomerTBDItemWarningMessage } from "./CustomerTBDItemWarningMessage";
import { getLeadTimeAndDeliveryDates } from "./utils";

// The interpolated block separates declarations with commas, so CSS reads it as one invalid
// `color` declaration and drops it: `color`, `background` and the padding are not applied.
const QuantityCell = styled(TableCell)`
	width: 40%;

	${({ color, background }: { color?: string; background?: string }) => {
		return css`
												color: ${color},
												background: ${background},
												padding: 10,
`;
	}}
`;

const DateCell = styled(TableCell)`
	width: 60%;
`;

/**
 * Renders the modal in `@ryerson/frontend.modal`'s `Modal`. Apply is disabled when there is no
 * common entry or it has no lead-time days. For a customer-part TBD line
 * (`canSelectFutureDeliveryDateForTBDItems`) the sales-rep message sits above the table for a
 * single lead-time day and below it for several.
 */
export const FutureDatesDesktop: React.FC<FutureDatesType> = ({
	filteredDeliveryTimes,
	handleApply,
	handleChange,
	isFetching,
	modalOpen,
	onClose,
	productsDeliveryTimes,
	rows,
	setModalOpen,
	showError,
	errorMessage,
	handleRemove,
	popperDisplayedState,
	triggerRef,
	titleOverride,
}) => {
	const { flexibleDelivery } = useEcommerce();
	const { theme } = useTheme();
	const { white, lighterGray, gray, text, darkGray, lightGray, link } = theme.colors.primary;
	const { darkBlue } = theme.colors.secondary;
	const {
		localization: { language },
	} = useApplication();
	const content = FutureDatesContent[language || "en"];

	const TableStyle = `
									display:block; 
									width: 100%;
									border-collapse: collapse;
									border-spacing: 0px;
									position: relative;
									overflow-y: auto;
									overflow-x: unset;
									max-height: 300px;
									border: solid ${lighterGray} 1px;
									width: 344px;
								`;

	const firstRow = rows[0];

	const leadTimeAndDeliveryDates =
		rows && getLeadTimeAndDeliveryDates(rows[0], productsDeliveryTimes);

	const hasMultipleDeliveryDates =
		leadTimeAndDeliveryDates && leadTimeAndDeliveryDates.leadTimeDays?.length > 1;

	return (
		<Flex css={{ borderColor: white, marginLeft: "-25px" }}>
			<Modal
				title={
					titleOverride ??
					(rows?.[0]?.canSelectFutureDeliveryDateForTBDItems && !hasMultipleDeliveryDates
						? content.deliveryDate
						: content.multipleDates)
				}
				closable={true}
				isOpen={modalOpen}
				onClose={() => {
					setModalOpen(false);
					onClose();
				}}
				css={css`
					div {
						overflow-y: visible;
					}
					.click-shield {
						div {
							max-width: 760px;
							max-height: 600px;
						}
					}
					.click-shield > div > div {
						padding-left: 20px;
						padding-right: 20px;
					}
				`}
				buttonArea={<></>}
			>
				<Flex css={{ gap: 20 }}>
					<Flex direction="column" css={{ overflow: "visible" }}>
						<Typography size="lg" color={darkBlue} css={{ marginBottom: 20 }}>
							{content.availableToShip}
						</Typography>
						{rows?.[0]?.canSelectFutureDeliveryDateForTBDItems &&
						!hasMultipleDeliveryDates ? (
							<CustomerTBDItemWarningMessage
								hasMultipleDeliveryDates={hasMultipleDeliveryDates}
								message={content.customerPartDeliveryDateMessage}
							/>
						) : (
							<Typography css={{ marginBottom: 40 }} color={darkGray}>
								{content.multipleShipments}
							</Typography>
						)}
						<Table addStyleString={TableStyle}>
							<TableHead
								css={css`
									display: table;
									width: 100%;
									table-layout: fixed;
									top: 0;
									position: sticky;
									z-index: ${IndexedLayers.base};
									height: 64px;
								`}
							>
								<TableRow
									css={css`
										background: ${gray};
										height: 60px;
									`}
								>
									<QuantityCell background={gray}>
										<Typography color={white}>{content.quantity}</Typography>
									</QuantityCell>
									<DateCell>
										<Typography
											css={{
												color: white,
												background: gray,
												padding: 10,
											}}
										>
											{content.date}
										</Typography>
									</DateCell>
								</TableRow>
							</TableHead>
							<TableBody>
								<>
									{rows
										.flatMap((rowData, rowDataIndex: number) => {
											const leadTimeAndDeliveryDates =
												getLeadTimeAndDeliveryDates(
													rowData,
													productsDeliveryTimes
												);

											return getShipDates(
												leadTimeAndDeliveryDates,
												flexibleDelivery
											).map((shipDate) => {
												return {
													...shipDate,
													quantityUOM: isShoppingCartDataType(rowData)
														? rowData.quantityUOM
														: rowData.quantityUom,
													rowDataIndex,
												};
											});
										})
										.map((shipDate, index: number) => {
											const {
												deliveryDate,
												deliveryQuantity: ShipDateQuantity,
												rowDataIndex,
											} = shipDate || {};
											const displayDate = deliveryDate || content.tbd;
											/* deliveryQuantity - For normal parts and multiple date customer TBD part deliveryTimes call returns deliveryQuantity, 
												but for single customer TBD part deliveryTimes call returns no deliveryQuantity, for that we are displaying row quantity
											*/
											const deliveryQuantity =
												rows[rowDataIndex]
													.canSelectFutureDeliveryDateForTBDItems &&
												!ShipDateQuantity
													? rows[rowDataIndex].quantity
													: shipDate.deliveryQuantity;

											return (
												<TableRow
													key={index}
													css={css`
														display: table;
														width: 100%;
														height: 60px;
														table-layout: fixed;
													`}
												>
													<QuantityCell>
														<FlexItem
															css={{
																padding: 10,
															}}
														>
															<Typography color={text}>
																{deliveryQuantity
																	? `${deliveryQuantity} ${getDisplayUom(
																			shipDate.quantityUOM,
																			"quantity"
																		)}`
																	: ""}
															</Typography>
														</FlexItem>
													</QuantityCell>
													<DateCell>
														<FlexItem
															css={{
																padding: 10,
															}}
														>
															<Typography color={text}>
																{displayDate}
															</Typography>
														</FlexItem>
													</DateCell>
												</TableRow>
											);
										})}
								</>
							</TableBody>
						</Table>
						{rows?.[0]?.canSelectFutureDeliveryDateForTBDItems &&
							hasMultipleDeliveryDates && (
								<CustomerTBDItemWarningMessage
									hasMultipleDeliveryDates={hasMultipleDeliveryDates}
									message={content.customerPartDeliveryDateMessage}
								/>
							)}
					</Flex>
					{/**
					 * TODO implement reduce function to be able to display multiple items’ dates
					 */}
					{firstRow !== undefined && (
						<Flex direction="column" style={datePickerStyle(theme, false)}>
							<Typography size="lg" color={darkBlue} css={{ marginBottom: 20 }}>
								{content.specifyFutureDate}
							</Typography>
							<Typography
								css={{
									marginBottom: showError ? 0 : 20,
								}}
								color={darkGray}
							>
								{content.selectADate}
							</Typography>
							<Flex
								css={{
									marginBottom: showError ? 0 : 20,
								}}
							>
								{showError && (
									<Alert severity={"error"}>
										<Typography color={darkGray} size={"sm"}>
											{errorMessage}
										</Typography>
									</Alert>
								)}
							</Flex>
							<Flex
								direction="row"
								css={{ gap: 20 }}
								alignItems="center"
								justifyContent="flex-start"
							>
								<div ref={triggerRef as RefObject<HTMLDivElement>}>
									<Flex
										css={{
											borderRadius: "5px",
											border: "solid 1px " + lightGray,
											padding: 4,
											zIndex: IndexedLayers.modal,
											width: 139,
										}}
									>
										<Typography color={darkGray}>
											<>
												<DatePickerPopper
													isFutureDates={true}
													id={firstRow.itemNumber}
													isFetching={isFetching}
													rowData={firstRow}
													isMobile={false}
													leadTimeAndDeliveryDates={filteredDeliveryTimes}
													handleOnDeliveryDateChange={handleChange}
													popperDisplayedState={popperDisplayedState}
													externalRef={triggerRef}
												/>
											</>
										</Typography>
									</Flex>
								</div>
								<Button
									size="sm"
									fullwidth
									label={content.apply}
									disabled={
										!filteredDeliveryTimes ||
										filteredDeliveryTimes.leadTimeDays.length === 0
									}
									onClick={function (
										e:
											| React.MouseEvent<Element, MouseEvent>
											| React.KeyboardEvent<Element>
									): void {
										handleApply();
									}}
								/>
							</Flex>
							<Flex
								direction="row"
								justifyContent="flex-start"
								css={{ gap: 10, marginTop: 20 }}
								alignItems="center"
								onClick={function (
									e:
										| React.MouseEvent<Element, MouseEvent>
										| React.KeyboardEvent<Element>
								): void {
									handleRemove();
								}}
							>
								<Icon icon={"trash"} color={link} size="sm" />
								<Typography color={link}>{content.remove}</Typography>
							</Flex>
						</Flex>
					)}
				</Flex>
			</Modal>
		</Flex>
	);
};
